import { Kokki } from './kokki';
import { BASE_URL, getAuthHeaders, readApiError } from './auth';

export type BotDifficulty = 'easy' | 'medium' | 'hard';

export interface BotProfile {
    id: number;
    name: string;
    difficulty: BotDifficulty;
    isActive: boolean;
    gameTypes: string[];
    kokkiId: number;
    kokki?: Kokki;
    accuracyOverride: number | null;
    baseReactionMs: number;
    readingMsPerChar: number;
    minActionDelayMs: number;
    maxActionDelayMs: number;
    timingJitterPct: number;
    powerupUseChance: number;
    targetLeaderChance: number;
    bankChance: number;
    riskTolerance: number;
    notes: string | null;
    powerupWeights: Record<string, number> | null;
    createdAt: string;
    updatedAt: string;
}

export interface BotProfilePayload {
    name: string;
    difficulty: BotDifficulty;
    isActive: boolean;
    gameTypes: string[];
    kokkiId: number;
    accuracyOverride?: number;
    baseReactionMs?: number;
    readingMsPerChar?: number;
    minActionDelayMs?: number;
    maxActionDelayMs?: number;
    timingJitterPct?: number;
    powerupUseChance?: number;
    targetLeaderChance?: number;
    bankChance?: number;
    riskTolerance?: number;
    notes?: string;
    powerupWeights?: Record<string, number>;
}

export type UpdateBotProfilePayload = Partial<BotProfilePayload>;

export const fetchBotProfiles = async (filters?: { gameType?: string; active?: boolean }): Promise<BotProfile[]> => {
    const params = new URLSearchParams();
    if (filters?.gameType) params.set('gameType', filters.gameType);
    if (filters?.active !== undefined) params.set('active', String(filters.active));

    const response = await fetch(`${BASE_URL}bots${params.toString() ? `?${params.toString()}` : ''}`, {
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw await readApiError(response, 'Failed to fetch bot profiles');
    return response.json();
};

export const createBotProfile = async (data: BotProfilePayload): Promise<BotProfile> => {
    const response = await fetch(`${BASE_URL}bots`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) throw await readApiError(response, 'Failed to create bot profile');
    return response.json();
};

export const updateBotProfile = async (id: number, data: UpdateBotProfilePayload): Promise<BotProfile> => {
    const response = await fetch(`${BASE_URL}bots/${id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
    });
    if (!response.ok) throw await readApiError(response, 'Failed to update bot profile');
    return response.json();
};

export const deleteBotProfile = async (id: number): Promise<void> => {
    const response = await fetch(`${BASE_URL}bots/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) throw await readApiError(response, 'Failed to delete bot profile');
};
